import type {
  LazyErrorBoundaryProps,
  LazyErrorBoundaryState,
} from "@/shared/types/component.types";
import { Component, ErrorInfo } from "react";

class LazyErrorBoundary extends Component<
  LazyErrorBoundaryProps,
  LazyErrorBoundaryState
> {
  constructor(props: LazyErrorBoundaryProps) {
    super(props);
    this.state = {
      hasError: false,
      retryCount: 0,
    };
  }

  static getDerivedStateFromError(error: Error): LazyErrorBoundaryState {
    return {
      hasError: true,
      error,
      retryCount: 0,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error("Lazy loading error caught by boundary:", error, errorInfo);
    this.props.onError?.(error, errorInfo);
  }

  handleRetry = () => {
    const { maxRetries = 3 } = this.props;
    const { retryCount } = this.state;

    if (retryCount < maxRetries) {
      this.setState({
        hasError: false,
        error: undefined,
        retryCount: retryCount + 1,
      });

      // Force a re-render by updating the key
      window.location.reload();
    }
  };

  render() {
    const { hasError, error, retryCount } = this.state;
    const { children, fallback, maxRetries = 3 } = this.props;

    if (hasError) {
      if (fallback) {
        return fallback;
      }

      return (
        <div className="flex flex-col items-center justify-center min-h-64 p-8">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-gray-800 mb-4">
              Failed to Load Component
            </h2>
            <p className="text-gray-600 mb-4">
              {error?.message || "An error occurred while loading this page."}
            </p>

            {retryCount < maxRetries && (
              <button
                onClick={this.handleRetry}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
              >
                Retry ({retryCount + 1}/{maxRetries})
              </button>
            )}

            {retryCount >= maxRetries && (
              <div className="text-center">
                <p className="text-red-600 mb-4">
                  Maximum retry attempts reached. Please refresh the page.
                </p>
                <button
                  onClick={() => window.location.reload()}
                  className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600 transition-colors"
                >
                  Refresh Page
                </button>
              </div>
            )}
          </div>
        </div>
      );
    }

    return children;
  }
}

export default LazyErrorBoundary;
